import { requestApi } from "@/server/apiService";
import { ApiEndpoints } from "@/server/apiEndpoints";

export type ChatKeyEpoch = {
  chat_id: string;
  epoch: number;
  created_at: number;
  created_by: string;
  /**
   * The caller's own encrypted CSK payload for this epoch. The server
   * projects this per-request, so each member sees only their own blob.
   * May be null/undefined if the caller has no payload (e.g., they were
   * removed from the chat before this epoch was rotated, or the epoch
   * has been purged).
   */
  self_payload?: string | null;
};

export type RotateInitResponse = {
  rotation_id: string;
  new_epoch: number;
};

export type RotatePayloadsResponse = {
  accepted_count: number;
  total_uploaded: number;
};

/**
 * Fetch a single epoch record for a chat.
 */
export const fetchEpoch = async (
  chatId: string,
  epoch: number,
): Promise<ChatKeyEpoch> => {
  const response = await requestApi({
    url: ApiEndpoints.EPOCHS,
    method: "GET",
    params: { chat_id: chatId, epoch },
  });
  return response.data;
};

/**
 * Fetch all available epoch records for a chat.
 */
export const fetchEpochs = async (chatId: string): Promise<ChatKeyEpoch[]> => {
  const response = await requestApi({
    url: ApiEndpoints.EPOCHS_LIST,
    method: "GET",
    params: { chat_id: chatId },
  });
  return response.data;
};

/**
 * Fetch pinned epoch numbers for a chat.
 */
export const fetchPinnedEpochs = async (chatId: string): Promise<number[]> => {
  const response = await requestApi({
    url: ApiEndpoints.EPOCHS_PINNED,
    method: "GET",
    params: { chat_id: chatId },
  });
  return response.data;
};

/**
 * Begin an epoch rotation. Server returns a rotation_id used to chunk-upload
 * payloads and then commit. `expectedCurrentEpoch` is what the client thinks
 * `current_epoch` is at the time of rotation; the server rejects with 409 if
 * it's stale, so the client can refresh and retry without uploading anything.
 */
export const initEpochRotation = async (
  chatId: string,
  expectedCurrentEpoch: number,
): Promise<RotateInitResponse> => {
  const response = await requestApi({
    url: ApiEndpoints.EPOCHS_ROTATE_INIT,
    method: "POST",
    data: { chat_id: chatId, expected_current_epoch: expectedCurrentEpoch },
  });
  return response.data;
};

/**
 * Upload a chunk of per-member encrypted payloads for an in-flight rotation.
 * Caller is expected to chunk by member count; see CHUNK_SIZE in the rotation
 * orchestration in middleware/chats.ts.
 */
export const uploadEpochPayloads = async (
  chatId: string,
  rotationId: string,
  payloads: Record<string, string>,
): Promise<RotatePayloadsResponse> => {
  const response = await requestApi({
    url: ApiEndpoints.EPOCHS_ROTATE_PAYLOADS,
    method: "POST",
    data: { chat_id: chatId, rotation_id: rotationId, payloads },
  });
  return response.data;
};

/**
 * Commit a rotation. Server validates that every current member has an
 * uploaded payload, advances `current_epoch`, and broadcasts `key_rotated`.
 */
export const commitEpochRotation = async (
  chatId: string,
  rotationId: string,
): Promise<void> => {
  await requestApi({
    url: ApiEndpoints.EPOCHS_ROTATE_COMMIT,
    method: "POST",
    data: { chat_id: chatId, rotation_id: rotationId },
  });
};

/**
 * Explicitly abort an in-flight rotation. Best-effort cleanup of pending
 * payload rows. The server will also reap stale rotations on the next init.
 */
export const abortEpochRotation = async (
  chatId: string,
  rotationId: string,
): Promise<void> => {
  await requestApi({
    url: ApiEndpoints.EPOCHS_ROTATE_ABORT,
    method: "POST",
    data: { chat_id: chatId, rotation_id: rotationId },
  });
};
